import { useState } from "react";
import { Copy, Check, Link2, AlertCircle, Building2, Coins } from "lucide-react";
import { apiAuth } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(email) {
  const errors = {};
  if (!email.trim()) {
    errors.email = "E-Mail-Adresse ist erforderlich.";
  } else if (!EMAIL_RE.test(email.trim())) {
    errors.email = "Bitte eine gültige E-Mail-Adresse eingeben.";
  }
  return errors;
}

export default function AdminMagicLink() {
  const [email, setEmail] = useState("");
  const [portalType, setPortalType] = useState("startup");
  const [touched, setTouched] = useState(false); // true after first submit attempt
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [copied, setCopied] = useState(false);

  const errors = validate(email);
  const hasErrors = Object.keys(errors).length > 0;

  // Show field error only after user has tried to submit, or after leaving the field
  const [emailTouched, setEmailTouched] = useState(false);
  const showEmailError = (touched || emailTouched) && !!errors.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (hasErrors) return;

    setLoading(true);
    setResult(null);
    setApiError(null);
    setCopied(false);
    try {
      const { data } = await apiAuth.post("/admin/magic-link/generate", {
        email: email.trim(),
        type: portalType,
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        detail === "no_startup_found_for_email"
          ? "Keine Startup-Registrierung für diese E-Mail gefunden."
          : detail === "no_investor_found_for_email"
          ? "Keine Investor-Registrierung für diese E-Mail gefunden."
          : detail || err?.message || "Unbekannter Fehler";
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.login_url) return;
    try {
      await navigator.clipboard.writeText(result.login_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* fallback */
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-cocoa">Magic Link Generator</h2>
        <p className="text-sm text-warmGrey mt-1">
          Erstellt einen Einmal-Login-Link für ein Portal — ohne E-Mail-Versand.
        </p>
      </div>

      <div className="wit-card p-6 space-y-5">
        <form onSubmit={handleSubmit} noValidate className="space-y-5">

          {/* Portal type toggle */}
          <div>
            <label className="block text-xs font-medium text-warmGrey mb-2 uppercase tracking-wide">
              Portal <span className="text-raspberry">*</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setPortalType("startup"); setResult(null); setApiError(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm border transition-colors ${
                  portalType === "startup"
                    ? "bg-roseMilk border-raspberry/40 text-cocoa font-medium"
                    : "bg-white border-sandBeige text-warmGrey hover:border-raspberry/30"
                }`}
              >
                <Building2 size={14} />
                Startup
              </button>
              <button
                type="button"
                onClick={() => { setPortalType("investor"); setResult(null); setApiError(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm border transition-colors ${
                  portalType === "investor"
                    ? "bg-roseMilk border-raspberry/40 text-cocoa font-medium"
                    : "bg-white border-sandBeige text-warmGrey hover:border-raspberry/30"
                }`}
              >
                <Coins size={14} />
                Investor
              </button>
            </div>
          </div>

          {/* Email input */}
          <div>
            <label
              htmlFor="ml-email"
              className="block text-xs font-medium text-warmGrey mb-2 uppercase tracking-wide"
            >
              E-Mail-Adresse <span className="text-raspberry">*</span>
            </label>
            <input
              id="ml-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setResult(null);
                setApiError(null);
              }}
              onBlur={() => setEmailTouched(true)}
              placeholder="z. B. founder@startup.com"
              className={`wit-input w-full transition-colors ${
                showEmailError
                  ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-200"
                  : email.trim() && !errors.email
                  ? "border-green-400 bg-green-50 focus:border-green-500"
                  : ""
              }`}
            />
            {showEmailError && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                <AlertCircle size={12} />
                {errors.email}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="wit-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Link2 size={15} />
            {loading ? "Generiere…" : "Magic Link erstellen"}
          </button>
        </form>

        {/* API Error */}
        {apiError && (
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 text-sm text-red-700">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{apiError}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3 p-4 rounded-2xl bg-green-50 border border-green-200">
            <div className="flex items-center gap-2 text-sm">
              <Check size={15} className="text-green-600 shrink-0" />
              <span className="font-medium text-cocoa">{result.label}</span>
              <span className="text-warmGrey">·</span>
              <span className="text-warmGrey">{result.email}</span>
            </div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={result.login_url}
                className="wit-input flex-1 text-xs font-mono text-warmGrey bg-white"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-2 rounded-2xl border border-green-300 bg-white hover:border-green-500 transition-colors"
                title="Kopieren"
              >
                {copied ? (
                  <Check size={15} className="text-green-600" />
                ) : (
                  <Copy size={15} className="text-warmGrey" />
                )}
              </button>
            </div>
            <p className="text-xs text-warmGrey">
              Link ist 30 Minuten gültig und kann nur einmal verwendet werden.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
